import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { useController } from "react-hook-form";
import * as React from "react";
import { Search, X, MapPin, Loader2, AlertCircle } from "lucide-react";

import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { HiddenField } from "@/components/fields/hidden-field";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ADDRESS_SEARCH_VALIDATION,
  ADDRESS_SEARCH_TIMING,
  ADDRESS_SEARCH_DEFAULTS,
  KEYBOARD_KEYS,
  ADDRESS_SEARCH_MESSAGES,
  ENTRY_METHODS,
} from "@/lib/address-search-constants";
import {
  AddressSearchTimeoutError,
  AddressSearchApiError,
  AddressSearchPredictionError,
  isAbortError,
  getUserFriendlyErrorMessage,
  logAddressSearchError,
} from "@/lib/address-search-errors";
import { useAddressSearchState } from "@/lib/address-search-state";
import type { CountryCode } from "@/lib/address-search-types";
import { cn } from "@/lib/utils";
import { FormTooltip } from "@/components/ui/form-description";

interface AddressSearchFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  country?: CountryCode;
  stateOptions?: { value: string; label: string }[];
  showAddressLine2?: boolean;
  postalCodeLabel?: string;
  postalCodePlaceholder?: string;
  searchDebounceMs?: number;
  maxResults?: number;
  apiEndpoint?: string;
  manualEntryCheckboxLabel?: string;
  manualEntryCheckboxDescription?: string;
  noResultsTitle?: string;
  noResultsDescription?: string;
  errorTitle?: string;
  errorDescription?: string;
  addressLine1Label?: string;
  addressLine1Placeholder?: string;
  addressLine2Label?: string;
  addressLine2Placeholder?: string;
  cityLabel?: string;
  cityPlaceholder?: string;
  stateLabel?: string;
  statePlaceholder?: string;
}

export function AddressSearchField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  country = "US",
  stateOptions,
  showAddressLine2 = true,
  searchDebounceMs = ADDRESS_SEARCH_TIMING.DEFAULT_DEBOUNCE_MS,
  maxResults = ADDRESS_SEARCH_DEFAULTS.MAX_RESULTS,
  apiEndpoint = ADDRESS_SEARCH_DEFAULTS.API_ENDPOINT,
  manualEntryCheckboxLabel = "Enter address manually",
  addressLine1Label = "Address Line 1",
  addressLine1Placeholder,
  addressLine2Label = "Address Line 2",
  addressLine2Placeholder,
  cityLabel = "City/Town",
  cityPlaceholder,
  stateLabel,
  statePlaceholder,
  postalCodeLabel = "Zip/Postal Code",
  postalCodePlaceholder,
  className,
  ...htmlProps
}: AddressSearchFieldProps<TFieldValues, TName>) {
  // Use consolidated state management hook
  const { state, actions, refs } = useAddressSearchState();
  const {
    searchQuery,
    predictions,
    showSuggestions,
    isLoading,
    hasSearched,
    hasError,
    errorMessage,
    selectedAddress,
    manualEntry,
    activeIndex,
    hasTyped,
  } = state;

  // Destructure refs for easier access
  const debounceTimerRef = refs.debounceTimer;
  const abortControllerRef = refs.abortController;
  const queryCache = refs.queryCache;

  // Additional refs not in state hook
  const searchRef = React.useRef<HTMLDivElement>(null);

  const baseFieldName = name as string;
  const listboxId = React.useId();
  const activeDescendantId =
    activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;

  // Adaptive debounce: shorter delay after first keystroke for better UX
  const adaptiveDebounceMs = hasTyped
    ? ADDRESS_SEARCH_TIMING.ADAPTIVE_DEBOUNCE_MS
    : searchDebounceMs;

  // Computed mode value - derived from state instead of duplicate state
  const mode: "search" | "manual" | "selected" = selectedAddress
    ? "selected"
    : manualEntry
      ? "manual"
      : "search";

  // Set default placeholders based on country if not provided
  const finalAddressLine1Placeholder =
    addressLine1Placeholder ?? getDefaultAddressLine1Placeholder(country);
  const finalAddressLine2Placeholder =
    addressLine2Placeholder ?? getDefaultAddressLine2Placeholder(country);
  const finalCityPlaceholder =
    cityPlaceholder ?? getDefaultCityPlaceholder(country);
  const finalStatePlaceholder =
    statePlaceholder ?? getDefaultStatePlaceholder(country);
  const finalPostalCodePlaceholder =
    postalCodePlaceholder ?? getDefaultPostalCodePlaceholder(country);

  // Use controller for validation field so we can update it properly
  const validationField = useController({
    control,
    name: `${baseFieldName}._validation` as FieldPath<TFieldValues>,
  });

  // Controllers for address fields to allow clearing on manual toggle
  // Note: useController is already optimized by react-hook-form internally
  const line1Controller = useController({
    control,
    name: `${baseFieldName}.address.line1` as FieldPath<TFieldValues>,
  });
  const line2Controller = useController({
    control,
    name: `${baseFieldName}.address.line2` as FieldPath<TFieldValues>,
  });
  const cityController = useController({
    control,
    name: `${baseFieldName}.address.city` as FieldPath<TFieldValues>,
  });
  const stateController = useController({
    control,
    name: `${baseFieldName}.address.state` as FieldPath<TFieldValues>,
  });
  const postalCodeController = useController({
    control,
    name: `${baseFieldName}.address.postalCode` as FieldPath<TFieldValues>,
  });

  // Update validation field whenever selection state changes
  React.useEffect(() => {
    const newValue = selectedAddress ? "valid" : manualEntry ? "manual" : "";
    validationField.field.onChange(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress, manualEntry]);

  // Debounced search function with AbortController for race condition prevention
  const searchAddress = React.useCallback(
    async (searchValue: string, signal: AbortSignal) => {
      // Validate and sanitize input
      const trimmed = searchValue.trim();

      // Validation: Check length constraints using constants
      if (
        !trimmed ||
        trimmed.length < ADDRESS_SEARCH_VALIDATION.MIN_QUERY_LENGTH ||
        trimmed.length > ADDRESS_SEARCH_VALIDATION.MAX_QUERY_LENGTH
      ) {
        actions.setPredictions([]);
        actions.setShowSuggestions(false);
        actions.setLoading(false);
        actions.setHasSearched(false);
        actions.setError(false, null);
        return;
      }

      // Basic sanitization: remove potentially dangerous characters
      // Server should do comprehensive validation, this is just client-side protection
      const sanitized = trimmed.replace(/[<>]/g, "");

      // Create cache key from sanitized input and country
      const cacheKey = `${sanitized}:${country}`;

      // Check cache first - deduplicate identical queries
      if (queryCache.current.has(cacheKey)) {
        const cachedResults = queryCache.current.get(cacheKey)!;
        actions.setPredictions(cachedResults);
        actions.setShowSuggestions(true);
        actions.setHasSearched(true);
        actions.setError(false, null);
        return;
      }

      actions.setLoading(true);
      actions.setError(false, null);

      try {
        // Create a timeout promise that will reject after configured timeout
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(
              new AddressSearchTimeoutError(
                ADDRESS_SEARCH_TIMING.REQUEST_TIMEOUT_MS,
              ),
            );
          }, ADDRESS_SEARCH_TIMING.REQUEST_TIMEOUT_MS);
        });

        // Race between fetch and timeout
        const response = await Promise.race([
          fetch(
            `${apiEndpoint}?input=${encodeURIComponent(sanitized)}&types=address&components=country:${country.toLowerCase()}`,
            {
              signal, // Pass abort signal to fetch
            },
          ),
          timeoutPromise,
        ]);

        if (!response.ok) {
          throw new AddressSearchApiError(response.status, response.statusText);
        }

        const data = await response.json();

        // Check if request was aborted before updating state
        if (signal.aborted) return;

        if (data.predictions && data.predictions.length > 0) {
          const results = data.predictions.slice(0, maxResults);

          // Cache the results for future identical queries
          queryCache.current.set(cacheKey, results);

          // Use atomic action for success
          actions.searchSuccess(results);
        } else {
          // Use atomic action for no results
          actions.searchNoResults();
        }
      } catch (error) {
        // Ignore abort errors (they're expected when cancelling requests)
        if (isAbortError(error)) {
          return;
        }

        // Log the error with context
        logAddressSearchError(error, "searchAddress", {
          searchValue: sanitized,
          country,
        });

        // Get user-friendly error message
        const friendlyMessage = getUserFriendlyErrorMessage(error);

        // Don't update state if request was aborted
        if (!signal.aborted) {
          // Use atomic action for error
          actions.searchError(friendlyMessage);
        }
      } finally {
        // Only update loading state if not aborted
        if (!signal.aborted) {
          actions.setLoading(false);
        }
      }
    },
    [apiEndpoint, country, maxResults, actions, queryCache],
  );

  // Debounced search on query change with request cancellation and adaptive timing
  React.useEffect(() => {
    // Mark that user has started typing (enables faster debounce)
    if (searchQuery && !hasTyped) {
      actions.setHasTyped(true);
    }

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear existing timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Debounce the search with adaptive timing
    debounceTimerRef.current = setTimeout(() => {
      searchAddress(searchQuery, abortController.signal);
    }, adaptiveDebounceMs);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      abortController.abort();
    };
    // debounceTimerRef and abortControllerRef are refs and stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchAddress, adaptiveDebounceMs, hasTyped, actions]);

  // Close suggestions when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        actions.setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [actions]);

  // Handle address selection with useCallback to prevent re-renders
  const handleSelectAddress = React.useCallback(
    async (placeId: string) => {
      try {
        const prediction = predictions.find((p) => p.place_id === placeId);

        if (!prediction) {
          throw new AddressSearchPredictionError(placeId);
        }

        // Parse the description - in production, you'd use Google Places Details API
        // Format: "123 Main Street, New York, NY 10001, USA"
        const parts = prediction.description.split(", ");

        // Extract state and postal code from the third part (e.g., "NY 10001")
        const stateZipPart = parts[2] || "";
        const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s+(.+)$/);

        const state = stateZipMatch ? stateZipMatch[1] : "";
        const postalCode = stateZipMatch ? stateZipMatch[2] : searchQuery;

        const addressDetails = {
          line1: parts[0] || "",
          line2: "",
          city: parts[1] || "",
          state,
          postalCode,
        };

        actions.setSelectedAddress(addressDetails);
        actions.setShowSuggestions(false);
        actions.setSearchQuery("");
        actions.setHasTyped(false); // Reset for next search
      } catch (error) {
        // Log the error with context
        logAddressSearchError(error, "handleSelectAddress", {
          placeId,
        });

        // Get user-friendly error message
        const friendlyMessage = getUserFriendlyErrorMessage(error);

        // Show error to user
        actions.setError(true, friendlyMessage);
        actions.setShowSuggestions(true);
      }
    },
    [predictions, searchQuery, actions],
  );

  // Handle removing selected address with useCallback
  const handleDismissAddress = React.useCallback(() => {
    actions.dismissAddress();
  }, [actions]);

  // Handle manual entry toggle with useCallback
  const handleManualEntryToggle = React.useCallback(
    (checked: boolean) => {
      actions.setManualEntry(checked);
      if (checked) {
        actions.resetSearch();
        actions.setSelectedAddress(null);

        // Clear all address fields when switching to manual mode
        line1Controller.field.onChange("");
        line2Controller.field.onChange("");
        cityController.field.onChange("");
        stateController.field.onChange("");
        postalCodeController.field.onChange("");
      }
    },
    [
      actions,
      line1Controller,
      line2Controller,
      cityController,
      stateController,
      postalCodeController,
    ],
  );

  return (
    <div className={cn("space-y-4", className)} {...htmlProps}>
      {/* Main Label and Description */}
      <div>
        <h3 className="text-sm leading-snug font-medium">
          {label}
          {required && (
            <>
              <span className="ms-1 text-red-500" aria-hidden="true">
                *
              </span>
              <span className="sr-only">({requiredLabel})</span>
            </>
          )}
          {description && descriptionAsTooltip && (
            <FormTooltip description={description} />
          )}
        </h3>
        {description && !descriptionAsTooltip && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      {/* Hidden field to track entry method */}
      <HiddenField
        control={control}
        name={`${baseFieldName}.entryMethod` as FieldPath<TFieldValues>}
        value={manualEntry ? ENTRY_METHODS.MANUAL : ENTRY_METHODS.AUTOMATIC}
      />

      {/* Hidden validation field - ensures form validation works */}
      <FormItem className="block gap-0">
        <FormControl>
          <input type="hidden" {...validationField.field} />
        </FormControl>
        <FormMessage>{validationField.fieldState.error?.message}</FormMessage>
      </FormItem>

      {/* Selected Address Display */}
      {mode === "selected" && selectedAddress && (
        <div className="space-y-4">
          <div className="rounded-md border bg-muted/50 p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-1 items-start gap-3">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="font-medium text-foreground">
                    Selected Address:
                  </p>
                  <p className="mt-1 text-sm text-foreground">
                    {selectedAddress.line1}
                  </p>
                  {selectedAddress.line2 && (
                    <p className="text-sm text-foreground">
                      {selectedAddress.line2}
                    </p>
                  )}
                  <p className="text-sm text-foreground">
                    {selectedAddress.city}, {selectedAddress.state}{" "}
                    {selectedAddress.postalCode}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleDismissAddress}
                className="shrink-0"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Dismiss address</span>
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Not correct?{" "}
            <button
              type="button"
              onClick={handleDismissAddress}
              className="font-medium text-primary hover:underline"
            >
              Try again
            </button>
          </p>
        </div>
      )}

      {/* Search Mode */}
      {mode === "search" && (
        <div className="space-y-4">
          <div ref={searchRef} className="relative">
            <Label
              htmlFor={`${baseFieldName}-search`}
              className="mb-2 block text-sm font-medium"
            >
              Search Address
            </Label>
            <div className="relative">
              <Input
                id={`${baseFieldName}-search`}
                type="text"
                role="combobox"
                aria-expanded={showSuggestions && predictions.length > 0}
                aria-controls={listboxId}
                aria-activedescendant={activeDescendantId}
                aria-autocomplete="list"
                placeholder="Start typing your address..."
                value={searchQuery}
                onChange={(e) => {
                  actions.setSearchQuery(e.target.value);
                  actions.setActiveIndex(-1);
                }}
                onFocus={() => {
                  if (predictions.length > 0 || hasSearched || hasError) {
                    actions.setShowSuggestions(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (!showSuggestions || predictions.length === 0) return;

                  switch (e.key) {
                    case KEYBOARD_KEYS.ARROW_DOWN:
                      e.preventDefault();
                      actions.setActiveIndex(
                        activeIndex < predictions.length - 1
                          ? activeIndex + 1
                          : activeIndex,
                      );
                      break;
                    case KEYBOARD_KEYS.ARROW_UP:
                      e.preventDefault();
                      actions.setActiveIndex(
                        activeIndex > 0 ? activeIndex - 1 : -1,
                      );
                      break;
                    case KEYBOARD_KEYS.ENTER:
                      e.preventDefault();
                      if (activeIndex >= 0 && predictions[activeIndex]) {
                        handleSelectAddress(predictions[activeIndex].place_id);
                        actions.setActiveIndex(-1);
                      }
                      break;
                    case KEYBOARD_KEYS.ESCAPE:
                      e.preventDefault();
                      actions.setShowSuggestions(false);
                      actions.setActiveIndex(-1);
                      break;
                    case KEYBOARD_KEYS.HOME:
                      if (predictions.length > 0) {
                        e.preventDefault();
                        actions.setActiveIndex(0);
                      }
                      break;
                    case KEYBOARD_KEYS.END:
                      if (predictions.length > 0) {
                        e.preventDefault();
                        actions.setActiveIndex(predictions.length - 1);
                      }
                      break;
                  }
                }}
                className="pr-10"
              />
              {isLoading ? (
                <Loader2 className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 animate-spin text-muted-foreground" />
              ) : (
                <Search className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              )}
            </div>

            {showSuggestions && (
              <div
                id={listboxId}
                role="listbox"
                className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-md"
              >
                {predictions.length > 0 ? (
                  predictions.map((suggestion, index) => (
                    <div
                      key={suggestion.place_id}
                      id={`${listboxId}-option-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      onClick={() => {
                        handleSelectAddress(suggestion.place_id);
                        actions.setActiveIndex(-1);
                      }}
                      onMouseEnter={() => actions.setActiveIndex(index)}
                      className={`w-full cursor-pointer border-b px-4 py-3 text-left text-sm text-popover-foreground transition-colors last:border-b-0 ${
                        index === activeIndex ? "bg-accent" : "hover:bg-accent"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>{suggestion.description}</span>
                      </div>
                    </div>
                  ))
                ) : hasError ? (
                  <div className="px-4 py-3 text-sm text-destructive">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        {errorMessage ||
                          "Unable to search addresses. Please try again later or enter your address manually using the checkbox below."}
                      </span>
                    </div>
                  </div>
                ) : hasSearched ? (
                  <div className="px-4 py-3 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        No addresses found. Try entering your address manually
                        using the checkbox below.
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Screen reader announcements */}
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            >
              {isLoading && ADDRESS_SEARCH_MESSAGES.SEARCHING}
              {!isLoading &&
                hasSearched &&
                predictions.length === 0 &&
                ADDRESS_SEARCH_MESSAGES.NO_RESULTS}
              {!isLoading && hasError && ADDRESS_SEARCH_MESSAGES.ERROR_LOADING}
              {!isLoading &&
                predictions.length > 0 &&
                ADDRESS_SEARCH_MESSAGES.RESULTS_FOUND(predictions.length)}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id={`${baseFieldName}-manual-mode-search`}
              checked={manualEntry}
              onCheckedChange={handleManualEntryToggle}
            />
            <Label
              htmlFor={`${baseFieldName}-manual-mode-search`}
              className="cursor-pointer text-sm font-normal"
            >
              {manualEntryCheckboxLabel}
            </Label>
          </div>
        </div>
      )}

      {/* Manual Entry Mode */}
      {mode === "manual" && (
        <div className="space-y-4">
          {/* Address Line 1 and 2 Row */}
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name={`${baseFieldName}.address.line1` as FieldPath<TFieldValues>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {addressLine1Label}
                    {required && (
                      <>
                        <span className="ms-1 text-red-500" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only">({requiredLabel})</span>
                      </>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={finalAddressLine1Placeholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address Line 2 */}
            {showAddressLine2 && (
              <FormField
                control={control}
                name={
                  `${baseFieldName}.address.line2` as FieldPath<TFieldValues>
                }
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {addressLine2Label}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={finalAddressLine2Placeholder}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* City and State Row */}
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name={`${baseFieldName}.address.city` as FieldPath<TFieldValues>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {cityLabel}
                    {required && (
                      <>
                        <span className="ms-1 text-red-500" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only">({requiredLabel})</span>
                      </>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={finalCityPlaceholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name={`${baseFieldName}.address.state` as FieldPath<TFieldValues>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {stateLabel ||
                      (country === "CA"
                        ? "State/Province"
                        : country === "GB"
                          ? "County"
                          : "State/Province")}
                    {required && (
                      <>
                        <span className="ms-1 text-red-500" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only">({requiredLabel})</span>
                      </>
                    )}
                  </FormLabel>
                  {stateOptions && stateOptions.length > 0 ? (
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={finalStatePlaceholder} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {stateOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={finalStatePlaceholder}
                        {...field}
                      />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Postal Code */}
          <FormField
            control={control}
            name={
              `${baseFieldName}.address.postalCode` as FieldPath<TFieldValues>
            }
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {postalCodeLabel}
                  {required && (
                    <>
                      <span className="ms-1 text-red-500" aria-hidden="true">
                        *
                      </span>
                      <span className="sr-only">({requiredLabel})</span>
                    </>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={finalPostalCodePlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Manual Entry Checkbox */}
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${baseFieldName}-manual-mode-manual`}
              checked={manualEntry}
              onCheckedChange={handleManualEntryToggle}
            />
            <Label
              htmlFor={`${baseFieldName}-manual-mode-manual`}
              className="cursor-pointer text-sm font-normal"
            >
              {manualEntryCheckboxLabel}
            </Label>
          </div>
        </div>
      )}

      {/* Hidden fields to store the selected address when using auto-lookup */}
      {selectedAddress && mode === "selected" && (
        <>
          <HiddenField
            control={control}
            name={`${baseFieldName}.address.line1` as FieldPath<TFieldValues>}
            value={selectedAddress.line1}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.address.line2` as FieldPath<TFieldValues>}
            value={selectedAddress.line2}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.address.city` as FieldPath<TFieldValues>}
            value={selectedAddress.city}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.address.state` as FieldPath<TFieldValues>}
            value={selectedAddress.state}
          />
          <HiddenField
            control={control}
            name={
              `${baseFieldName}.address.postalCode` as FieldPath<TFieldValues>
            }
            value={selectedAddress.postalCode}
          />
        </>
      )}
    </div>
  );
}

function getDefaultPostalCodePlaceholder(country: string): string {
  switch (country) {
    case "US":
      return "12345";
    case "CA":
      return "A1A 1A1";
    case "GB":
      return "SW1A 1AA";
    case "AU":
      return "2000";
    default:
      return "";
  }
}

function getDefaultAddressLine1Placeholder(country: string): string {
  switch (country) {
    case "US":
      return "123 Main Street";
    case "CA":
      return "123 Main Street";
    case "GB":
      return "123 High Street";
    case "AU":
      return "123 Main Street";
    default:
      return "";
  }
}

function getDefaultAddressLine2Placeholder(country: string): string {
  switch (country) {
    case "US":
      return "Apt 4B";
    case "CA":
      return "Unit 4B";
    case "GB":
      return "Flat 4B";
    case "AU":
      return "Unit 4B";
    default:
      return "";
  }
}

function getDefaultCityPlaceholder(country: string): string {
  switch (country) {
    case "US":
      return "New York";
    case "CA":
      return "Toronto";
    case "GB":
      return "London";
    case "AU":
      return "Sydney";
    default:
      return "";
  }
}

function getDefaultStatePlaceholder(country: string): string {
  switch (country) {
    case "US":
      return "Select state";
    case "CA":
      return "Select province";
    case "GB":
      return "Greater London";
    case "AU":
      return "Select state";
    default:
      return "";
  }
}
